import { supabase } from "@/integrations/supabase/client";

const db = supabase as any;

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export interface ErpContract {
  id: string;
  contract_number: string | null;
  contract_type: string;
  lead_id: string | null;
  listing_id: string | null;
  client_name: string;
  property_title: string | null;
  value: number;
  commission_pct: number;
  commission_value: number | null;
  status: string;
  signed_at: string | null;
  closed_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ErpCommission {
  id: string;
  contract_id: string | null;
  beneficiary: string;
  role: string;
  percentage: number;
  amount: number;
  status: string;
  due_date: string | null;
  paid_at: string | null;
  notes: string | null;
  created_at: string;
}

export interface ErpTransaction {
  id: string;
  kind: string;
  category: string;
  description: string;
  amount: number;
  due_date: string | null;
  paid_at: string | null;
  status: string;
  contract_id: string | null;
  notes: string | null;
  created_at: string;
}

export interface ErpAfterSale {
  id: string;
  contract_id: string | null;
  lead_id: string | null;
  client_name: string;
  stage: string;
  checklist: { label: string; done: boolean }[];
  satisfaction: number | null;
  next_action_at: string | null;
  notes: string | null;
  created_at: string;
}

export interface CrmAiInsight {
  id: string;
  lead_id: string | null;
  insight_type: string;
  summary: string;
  suggested_score: number | null;
  suggested_classification: string | null;
  next_steps: string | null;
  payload: Record<string, unknown>;
  created_at: string;
}

/* ------------------------------------------------------------------ */
/* Catalogs                                                            */
/* ------------------------------------------------------------------ */

export const CONTRACT_TYPES = [
  { value: "venda", label: "Venda" },
  { value: "locacao", label: "Locação" },
  { value: "permuta", label: "Permuta" },
  { value: "administracao", label: "Administração" },
];

export const CONTRACT_STATUS = [
  { value: "em_negociacao", label: "Em negociação" },
  { value: "proposta_aceita", label: "Proposta aceita" },
  { value: "assinado", label: "Assinado" },
  { value: "financiamento", label: "Financiamento" },
  { value: "escritura", label: "Escritura" },
  { value: "concluido", label: "Concluído" },
  { value: "cancelado", label: "Cancelado" },
];

export const COMMISSION_ROLES = [
  { value: "captador", label: "Captador" },
  { value: "vendedor", label: "Vendedor" },
  { value: "imobiliaria", label: "Imobiliária" },
  { value: "parceiro", label: "Parceiro" },
  { value: "indicacao", label: "Indicação" },
];

export const PAYMENT_STATUS = [
  { value: "pendente", label: "Pendente" },
  { value: "pago", label: "Pago" },
  { value: "cancelado", label: "Cancelado" },
];

export const TRANSACTION_CATEGORIES = [
  "Comissão", "Aluguel", "Marketing", "Google Ads", "Meta Ads", "Salário",
  "Imposto", "Cartório", "Software", "Escritório", "Outro",
];

export const AFTER_SALE_STAGES = [
  { value: "entrega_chaves", label: "Entrega de chaves" },
  { value: "documentacao_final", label: "Documentação final" },
  { value: "rgi", label: "RGI / Registro" },
  { value: "acompanhamento", label: "Acompanhamento" },
  { value: "indicacao", label: "Pedido de indicação" },
  { value: "concluido", label: "Concluído" },
];

export const DEFAULT_AFTER_SALE_CHECKLIST = [
  "Chaves entregues",
  "Escritura registrada no RGI",
  "Transferência de IPTU",
  "Transferência de água/luz/gás",
  "Cadastro no condomínio",
  "Pesquisa de satisfação enviada",
  "Pedido de indicação realizado",
];

export const label = (list: { value: string; label: string }[], v: string) =>
  list.find((i) => i.value === v)?.label || v;

export const brl = (v: number | null | undefined) =>
  typeof v === "number"
    ? v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 })
    : "—";

export const dateBR = (iso: string | null) =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("pt-BR") : "—";

/* ------------------------------------------------------------------ */
/* Data access                                                         */
/* ------------------------------------------------------------------ */

export async function fetchContracts(): Promise<ErpContract[]> {
  const { data } = await db.from("erp_contracts").select("*").order("created_at", { ascending: false });
  return (data || []) as ErpContract[];
}

export async function fetchCommissions(): Promise<ErpCommission[]> {
  const { data } = await db.from("erp_commissions").select("*").order("created_at", { ascending: false });
  return (data || []) as ErpCommission[];
}

export async function fetchTransactions(): Promise<ErpTransaction[]> {
  const { data } = await db.from("erp_transactions").select("*").order("due_date", { ascending: true });
  return (data || []) as ErpTransaction[];
}

export async function fetchAfterSales(): Promise<ErpAfterSale[]> {
  const { data } = await db.from("erp_after_sales").select("*").order("created_at", { ascending: false });
  return (data || []) as ErpAfterSale[];
}

export async function fetchInsights(): Promise<CrmAiInsight[]> {
  const { data } = await db
    .from("crm_ai_insights")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(200);
  return (data || []) as CrmAiInsight[];
}

export const insertRow = (table: string, row: Record<string, unknown>) =>
  db.from(table).insert(row).select("*").single();

export const updateRow = (table: string, id: string, patch: Record<string, unknown>) =>
  db.from(table).update(patch).eq("id", id);

export const deleteRow = (table: string, id: string) => db.from(table).delete().eq("id", id);

/** Creates a contract and, in sequence, the commission split + the receivable. */
export async function createContractWithFinance(
  contract: Partial<ErpContract>,
  brokerName?: string,
) {
  const value = Number(contract.value || 0);
  const pct = Number(contract.commission_pct ?? 6);
  const commission = (value * pct) / 100;

  const { data, error } = await insertRow("erp_contracts", {
    ...contract,
    value,
    commission_pct: pct,
    commission_value: commission,
  });
  if (error || !data) return { error };

  await insertRow("erp_commissions", {
    contract_id: data.id,
    beneficiary: brokerName || "TopClass Imóveis Premium",
    role: brokerName ? "vendedor" : "imobiliaria",
    percentage: pct,
    amount: commission,
    status: "pendente",
    due_date: contract.signed_at || null,
  });

  await insertRow("erp_transactions", {
    kind: "receita",
    category: "Comissão",
    description: `Comissão — ${contract.client_name || "contrato"}`,
    amount: commission,
    status: "pendente",
    contract_id: data.id,
    due_date: contract.signed_at || null,
  });

  return { data: data as ErpContract };
}

/* ------------------------------------------------------------------ */
/* Financial summary                                                   */
/* ------------------------------------------------------------------ */

export function financeSummary(txs: ErpTransaction[]) {
  const sum = (f: (t: ErpTransaction) => boolean) =>
    txs.filter(f).reduce((a, t) => a + Number(t.amount || 0), 0);
  const receitaPaga = sum((t) => t.kind === "receita" && t.status === "pago");
  const receitaPrevista = sum((t) => t.kind === "receita" && t.status === "pendente");
  const despesaPaga = sum((t) => t.kind === "despesa" && t.status === "pago");
  const despesaPrevista = sum((t) => t.kind === "despesa" && t.status === "pendente");
  return {
    receitaPaga,
    receitaPrevista,
    despesaPaga,
    despesaPrevista,
    saldo: receitaPaga - despesaPaga,
    saldoProjetado: receitaPaga + receitaPrevista - despesaPaga - despesaPrevista,
  };
}
